<template>
  <div class="smart-agent">
    <button
      v-if="!opened"
      class="smart-agent__float"
      type="button"
      aria-label="打开智慧体"
      @click="openAgent"
    >
      <span class="smart-agent__float-icon">AI</span>
      <span class="smart-agent__float-text">智慧体</span>
    </button>

    <section v-else class="smart-agent__panel" aria-label="智慧体聊天窗口">
      <div class="smart-agent__header">
        <div>
          <div class="smart-agent__title">CSC 智慧体</div>
          <div class="smart-agent__subtitle">留学问题即时问答</div>
        </div>
        <button class="smart-agent__close" type="button" aria-label="关闭智慧体" @click="opened = false">×</button>
      </div>

      <div ref="messageBox" class="smart-agent__messages">
        <div
          v-for="message in messages"
          :key="message.id"
          class="smart-agent__message"
          :class="'is-' + message.role"
        >
          <div class="smart-agent__avatar">{{ message.role === 'user' ? '我' : 'AI' }}</div>
          <div class="smart-agent__bubble">
            <div class="smart-agent__content">{{ message.content }}</div>
            <div v-if="message.refs && message.refs.length" class="smart-agent__refs">
              <div class="smart-agent__refs-title">相关答案</div>
              <button
                v-for="item in message.refs"
                :key="item.id"
                class="smart-agent__ref"
                type="button"
                @click="ask(item.title)"
              >
                {{ item.title }}
              </button>
            </div>
          </div>
        </div>
        <div v-if="loading" class="smart-agent__message is-assistant">
          <div class="smart-agent__avatar">AI</div>
          <div class="smart-agent__bubble">
            <div class="smart-agent__typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <div class="smart-agent__suggestions">
        <button v-for="item in suggestions" :key="item" type="button" @click="ask(item)">
          {{ item }}
        </button>
      </div>

      <div class="smart-agent__composer">
        <textarea
          v-model.trim="question"
          rows="1"
          maxlength="2000"
          placeholder="输入你的留学问题..."
          @keydown.enter.exact.prevent="send"
        ></textarea>
        <button type="button" :disabled="loading || !question" @click="send">发送</button>
      </div>
    </section>
  </div>
</template>

<script>
import { intl_student_faq_search } from '@/api/smartAgent';

let messageId = 0;

export default {
  name: 'SmartAgent',
  data() {
    return {
      opened: false,
      loading: false,
      question: '',
      suggestions: ['申请流程怎么走？', '课程证书如何获取？', '活动报名需要注意什么？'],
      messages: [
        {
          id: ++messageId,
          role: 'assistant',
          content: '你好，我是 CSC 智慧体。你可以问我留学申请、课程学习、证书、活动报名等问题。',
        },
      ],
    };
  },
  methods: {
    openAgent() {
      this.opened = true;
      this.$nextTick(this.scrollToBottom);
    },
    ask(text) {
      this.question = text;
      this.send();
    },
    async send() {
      const text = this.question.trim();
      if (!text || this.loading) return;

      this.messages.push({ id: ++messageId, role: 'user', content: text });
      this.question = '';
      this.loading = true;
      this.$nextTick(this.scrollToBottom);

      try {
        const res = await intl_student_faq_search({ q: text });
        const answer = this.buildAnswer(res && res.data);
        this.messages.push({
          id: ++messageId,
          role: 'assistant',
          content: answer.content,
          refs: answer.refs,
        });
      } catch (e) {
        this.messages.push({
          id: ++messageId,
          role: 'assistant',
          content: '服务暂时开小差了，请稍后再试，或换一种问法继续提问。',
        });
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      }
    },
    buildAnswer(data) {
      const items = data && Array.isArray(data.items) ? data.items : [];
      if (!data || !data.has_match || !items.length) {
        return {
          content: '你的问题我们暂时没有答案，联系我们csctalent@csc.edu.cn会尽快补充进来答案的。',
          refs: [],
        };
      }

      const first = items[0];
      return {
        content: first.body || first.title || '已找到相关答案，但内容暂时为空。',
        refs: items.slice(1),
      };
    },
    scrollToBottom() {
      const box = this.$refs.messageBox;
      if (box) box.scrollTop = box.scrollHeight;
    },
  },
};
</script>

<style lang="scss" scoped>
.smart-agent {
  position: fixed;
  right: 30px;
  bottom: 86px;
  z-index: 3000;
  font-family: Microsoft YaHe, Avenir, Helvetica, Arial, sans-serif;
}

.smart-agent__float {
  width: 78px;
  height: 78px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(145deg, #1768e8 0%, #10a37f 100%);
  box-shadow: 0 16px 36px rgba(20, 93, 180, 0.28);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.smart-agent__float-icon {
  font-size: 22px;
  font-weight: 700;
  line-height: 24px;
}

.smart-agent__float-text {
  margin-top: 3px;
  font-size: 13px;
}

.smart-agent__panel {
  width: 420px;
  height: 620px;
  max-height: calc(100vh - 120px);
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  box-shadow: 0 24px 58px rgba(22, 38, 62, 0.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.smart-agent__header {
  height: 68px;
  padding: 0 18px;
  color: #fff;
  background: linear-gradient(135deg, #1768e8 0%, #10a37f 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.smart-agent__title {
  font-size: 18px;
  font-weight: 700;
}

.smart-agent__subtitle {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.85;
}

.smart-agent__close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  font-size: 26px;
  line-height: 30px;
  background: rgba(255, 255, 255, 0.18);
}

.smart-agent__messages {
  flex: 1;
  min-height: 0;
  padding: 18px;
  overflow-y: auto;
  background: #f7f9fc;
}

.smart-agent__message {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.smart-agent__message.is-user {
  flex-direction: row-reverse;
}

.smart-agent__avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
  background: #10a37f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.smart-agent__message.is-user .smart-agent__avatar {
  background: #1768e8;
}

.smart-agent__bubble {
  max-width: 292px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff;
  color: #27364b;
  box-shadow: 0 4px 14px rgba(21, 40, 66, 0.06);
}

.smart-agent__message.is-user .smart-agent__bubble {
  color: #fff;
  background: #1768e8;
}

.smart-agent__content {
  white-space: pre-line;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.7;
}

.smart-agent__refs {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #edf1f6;
}

.smart-agent__refs-title {
  margin-bottom: 8px;
  color: #7b8798;
  font-size: 12px;
}

.smart-agent__ref {
  width: 100%;
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #dce8f8;
  border-radius: 6px;
  color: #1768e8;
  background: #f7fbff;
  cursor: pointer;
  text-align: left;
}

.smart-agent__suggestions {
  padding: 10px 14px 0;
  background: #fff;
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.smart-agent__suggestions button {
  flex: 0 0 auto;
  padding: 7px 10px;
  border: 1px solid #dce8f8;
  border-radius: 6px;
  color: #1768e8;
  background: #f7fbff;
  cursor: pointer;
  font-size: 12px;
}

.smart-agent__composer {
  padding: 12px 14px 14px;
  background: #fff;
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.smart-agent__composer textarea {
  flex: 1;
  height: 44px;
  max-height: 96px;
  padding: 11px 12px;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 20px;
}

.smart-agent__composer textarea:focus {
  border-color: #1768e8;
}

.smart-agent__composer button {
  width: 72px;
  height: 44px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #1768e8;
  cursor: pointer;
}

.smart-agent__composer button:disabled {
  cursor: not-allowed;
  background: #aeb8c6;
}

.smart-agent__typing {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 24px;
}

.smart-agent__typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9aa7b8;
  animation: smart-agent-pulse 1s infinite ease-in-out;
}

.smart-agent__typing span:nth-child(2) {
  animation-delay: 0.12s;
}

.smart-agent__typing span:nth-child(3) {
  animation-delay: 0.24s;
}

@keyframes smart-agent-pulse {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-4px);
  }
}
</style>
